import { useState, useEffect } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '@/store/hooks';
import {
  deleteUserById,
  generatePasswordResetLink,
  getUserById,
  updateUserById,
  userQueryKeys,
} from '@/features/users/api';
import { getCurrentUser } from '@/features/auth/api';
import { getTenantOptions, getBranchOptions } from '@/features/value-lists/api';
import type {
  ValueListItem,
  BranchValueListItem,
} from '@/features/value-lists/types';
import { hasPermission } from '@/lib/auth/permissions';

import EditableTextField from '@/components/ui/editable-text-field';
import EditableSelectField from '@/components/ui/editable-select-field';
import UserBranchesTab from '@/components/admin/UserBranchesTab';
import UserPermissionsTab from '@/components/admin/UserPermissionsTab';
import type { UpdateUser, UserType } from '@/features/users/user';
import { PageShell } from '@/components/layout/PageShell';
import { PageExpressiveLoading } from '@/components/feedback';

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  User as UserIcon,
  ShieldAlert,
  Trash2,
  UserMinus,
  Mail,
  Link as LinkIcon,
  Copy,
  Check,
  Building,
} from '@/lib/icons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_layout/users/$userId')({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const params = useParams({ from: '/_layout/users/$userId' });
  const userId = params.userId;
  const [activeTab, setActiveTab] = useState('details');
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [tenants, setTenants] = useState<ValueListItem[]>([]);
  const [branches, setBranches] = useState<BranchValueListItem[]>([]);
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((state) => state.auth.user);

  // Reading yourself is not an act on the `users` scope, which is administrative
  // and held only by tenant admins. Self-view is served from /auth/me so every
  // operator keeps their own profile page.
  const isSelf = currentUser?.id === userId;
  const isAuthorized = isSelf || hasPermission(currentUser, 'users', 'read');
  const canEdit = hasPermission(currentUser, 'users', 'update');
  const canDelete = hasPermission(currentUser, 'users', 'delete');
  const canGeneratePasswordReset = hasPermission(
    currentUser,
    'users',
    'update'
  );
  const canManageTenants = hasPermission(currentUser, 'tenants', 'update');

  const { data: userResult, isLoading: loading } = useQuery({
    queryKey: userQueryKeys.detail(userId),
    queryFn: () => (isSelf ? getCurrentUser() : getUserById(userId)),
    enabled: isAuthorized,
  });
  const user = userResult?.value ?? null;

  // Branch assignment only means something for a branch-scoped operator: a
  // tenant admin already reaches every branch and a superuser every tenant,
  // so the tab is hidden rather than shown with no effect.
  const showBranchesTab =
    user?.userType !== 'admin' && user?.userType !== 'superuser';

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUser) => updateUserById(userId, data),
    onSuccess: (result) => {
      if (result.succeeded && result.value) {
        queryClient.setQueryData(userQueryKeys.detail(userId), result);
        void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUserById(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
  });

  const deleteDialog = useConfirmDialog({
    title: t('users.deleteDialog.title'),
    description: '',
    confirmText: t('common.actions.delete'),
    cancelText: t('common.actions.cancel'),
    variant: 'destructive',
  });

  useEffect(() => {
    if (canManageTenants) {
      getTenantOptions().then((result) => {
        if (result.succeeded && result.value) {
          setTenants(result.value);
        }
      });
    }
  }, [canManageTenants]);

  useEffect(() => {
    if (!user?.tenantId) return;
    getBranchOptions(user.tenantId).then((result) => {
      if (result.succeeded && result.value) {
        setBranches(result.value);
      }
    });
  }, [user?.tenantId]);

  const tenantName =
    tenants.find((tn) => tn.id === user?.tenantId)?.name ??
    (user?.tenantId ? `ID: ${user.tenantId}` : t('users.detail.noTenant'));
  const branchName =
    branches.find((b) => b.id === user?.branchId)?.name ??
    (user?.branchId ? `ID: ${user.branchId}` : t('users.detail.noBranch'));

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
        <p className="text-muted-foreground">
          {t('common.noPermissionToView')}
        </p>
      </div>
    );
  }

  const handleBack = () => {
    window.history.back();
  };

  const handleDeactivate = () => {
    const newStatus = user?.isActive === false ? true : false;
    updateMutation.mutate({ isActive: newStatus });
  };

  const handleDelete = async () => {
    if (!user) return;
    const confirmed = await deleteDialog.confirm();
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(user.id);
      navigate({ to: '/users' });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : 'Delete failed',
        variant: 'destructive',
      });
    }
  };

  const canDeleteUser = () => {
    if (!user || !currentUser) return false;
    if (user.id === currentUser.id) return false;
    return canDelete;
  };

  const handleGenerateResetLink = async () => {
    if (!user) return;
    setIsGeneratingLink(true);
    try {
      const result = await generatePasswordResetLink(user.id);
      if (result.succeeded && result.value) {
        setResetLink(result.value);
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('common.error'),
          variant: 'destructive',
        });
      }
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!resetLink) return;
    const fullMessage = `${t('users.passwordReset.message')} ${resetLink}`;
    await navigator.clipboard.writeText(fullMessage);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleFieldUpdate = async (field: string, value: string) => {
    if (!user) return;
    const data: UpdateUser = {};
    switch (field) {
      case 'firstName':
        data.firstName = value;
        break;
      case 'lastName':
        data.lastName = value;
        break;
      case 'email':
        data.email = value;
        break;
      case 'phoneNumber':
        data.phoneNumber = value;
        break;
      case 'status':
        data.isActive = value === 'active';
        break;
      case 'userType':
        // Schema only accepts "admin" / "user"; the dropdown can never
        // produce anything else, but narrow it explicitly so a future code
        // path can't silently send an invalid value.
        if (value === 'admin' || value === 'user') {
          data.userType = value as UserType;
        }
        break;
      default:
        return;
    }
    if (Object.keys(data).length > 0) {
      await updateMutation.mutateAsync(data);
    }
  };

  if (loading) {
    return <PageExpressiveLoading text={t('users.loading')} />;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">{t('users.notFound')}</h1>
        <p className="text-muted-foreground">{t('users.noUserFound')}</p>
      </div>
    );
  }

  return (
    <PageShell
      title={`${user.firstName} ${user.lastName}`}
      description={`${user.email || user.phoneNumber || ''} • ${user.role?.name ?? t('common.notdefined')}`}
      actions={
        <>
          <Button variant="outline" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {canEdit && (
            <Button variant="outline" onClick={handleDeactivate}>
              <UserMinus className="mr-1 h-4 w-4" />
              {user.isActive !== false
                ? t('common.actions.deactivate')
                : t('common.actions.activate')}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="destructive"
              disabled={!canDeleteUser() || deleteMutation.isPending}
              onClick={handleDelete}
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {deleteMutation.isPending
                ? t('common.actions.deleting')
                : t('common.actions.delete')}
            </Button>
          )}
        </>
      }
    >
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Badge variant={user.isActive !== false ? 'default' : 'secondary'}>
              {user.isActive !== false
                ? t('common.status.active')
                : t('common.status.inactive')}
            </Badge>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="details">
                {t('common.tabs.basicinfo')}
              </TabsTrigger>
              <TabsTrigger value="permissions">
                {t('common.tabs.permissions')}
              </TabsTrigger>
              {showBranchesTab && (
                <TabsTrigger value="branches">
                  {t('common.tabs.branches', 'Branches')}
                </TabsTrigger>
              )}
              <TabsTrigger value="activity">
                {t('common.tabs.activity')}
              </TabsTrigger>
            </TabsList>
            <div className="mt-4">
              <TabsContent value="details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <UserIcon className="h-5 w-5 mr-2" />
                        {t('common.personalInfo')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <EditableTextField
                        value={user.firstName}
                        onSave={(value) =>
                          handleFieldUpdate('firstName', value)
                        }
                        label={t('common.form.firstName')}
                        type="text"
                        icon={<UserIcon className="h-4 w-4" />}
                        disabled={!canEdit}
                      />
                      <EditableTextField
                        value={user.lastName}
                        onSave={(value) => handleFieldUpdate('lastName', value)}
                        label={t('common.form.lastName')}
                        type="text"
                        icon={<UserIcon className="h-4 w-4" />}
                        disabled={!canEdit}
                      />
                      <EditableTextField
                        value={user.email || ''}
                        onSave={(value) => handleFieldUpdate('email', value)}
                        label={t('common.form.email')}
                        type="email"
                        icon={<Mail className="h-4 w-4" />}
                        disabled={!canEdit}
                      />
                      <EditableTextField
                        value={user.phoneNumber ?? ''}
                        onSave={(value) =>
                          handleFieldUpdate('phoneNumber', value)
                        }
                        label={t('common.form.phoneNumber')}
                        type="text"
                        disabled={!canEdit}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <ShieldAlert className="h-5 w-5 mr-2" />
                        {t('users.accessControl')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <EditableSelectField
                        value={user.userType ?? 'user'}
                        onSave={(value) => handleFieldUpdate('userType', value)}
                        label={t('common.role')}
                        options={[
                          { key: 'admin', label: 'Tenant Admin' },
                          { key: 'user', label: 'User' },
                        ]}
                        disabled={!canEdit}
                      />
                      <EditableSelectField
                        value={user.isActive !== false ? 'active' : 'inactive'}
                        onSave={(value) => handleFieldUpdate('status', value)}
                        label={t('common.status.title')}
                        options={[
                          { key: 'active', label: t('common.status.active') },
                          {
                            key: 'inactive',
                            label: t('common.status.inactive'),
                          },
                        ]}
                        disabled={!canEdit}
                      />
                      {canGeneratePasswordReset && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <h3 className="text-sm font-medium">
                              {t('users.passwordReset.title')}
                            </h3>
                            {!resetLink ? (
                              <Button
                                variant="outline"
                                onClick={handleGenerateResetLink}
                                disabled={isGeneratingLink}
                                className="w-full"
                              >
                                <LinkIcon className="h-4 w-4 mr-2" />
                                {isGeneratingLink
                                  ? t('users.passwordReset.generating')
                                  : t('users.passwordReset.generate')}
                              </Button>
                            ) : (
                              <div className="space-y-2">
                                <Alert>
                                  <LinkIcon className="h-4 w-4" />
                                  <AlertTitle>
                                    {t('users.passwordReset.success')}
                                  </AlertTitle>
                                  <AlertDescription>
                                    {t('users.passwordReset.copyInstruction')}
                                  </AlertDescription>
                                </Alert>
                                <div className="space-y-2">
                                  <textarea
                                    value={`${t('users.passwordReset.message')} ${resetLink}`}
                                    readOnly
                                    rows={4}
                                    className="w-full px-3 py-2 text-sm border rounded-md bg-muted resize-none"
                                  />
                                  <div className="flex justify-end">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleCopyLink}
                                    >
                                      {isCopied ? (
                                        <>
                                          <Check className="h-4 w-4 mr-1" />
                                          {t('common.copied')}
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="h-4 w-4 mr-1" />
                                          {t('common.copy')}
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Building className="h-5 w-5 mr-2" />
                        {t('users.detail.tenantInfo')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Read-only: PATCH /v1/users/{id} refuses tenantId by
                          design — moving a user between tenants is an
                          operators-surface action. It used to render an editor
                          whose save was silently discarded. */}
                      <div>
                        <h3 className="font-medium text-sm text-muted-foreground mb-1">
                          {t('users.detail.tenant')}
                        </h3>
                        <p className="text-sm">{tenantName}</p>
                      </div>
                      {/* Read-only: there is no branchId column on the
                          platform User row. Branch assignment is
                          PUT /v1/users/{id}/branches. */}
                      {(
                        <div>
                          <h3 className="font-medium text-sm text-muted-foreground mb-1">
                            {t('users.detail.branch')}
                          </h3>
                          <p className="text-sm">{branchName}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <UserIcon className="h-5 w-5 mr-2" />
                        {t('users.systemInfo')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h3 className="font-medium text-sm text-muted-foreground mb-1">
                          {t('users.fields.id')}
                        </h3>
                        <p className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                          {user.id}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              <TabsContent value="permissions">
                <UserPermissionsTab
                  userId={userId}
                  isVisible={activeTab === 'permissions'}
                />
              </TabsContent>
              {showBranchesTab && (
                <TabsContent value="branches">
                  <UserBranchesTab
                    userId={userId}
                    isVisible={activeTab === 'branches'}
                  />
                </TabsContent>
              )}
              <TabsContent value="activity">
                <Alert>
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>{t('common.tabs.activity')}</AlertTitle>
                  <AlertDescription>
                    {t('users.activity.description')}
                  </AlertDescription>
                </Alert>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
        <CardFooter className="border-t pt-5">
          <p className="text-xs text-muted-foreground">{user.id}</p>
        </CardFooter>
      </Card>

      <deleteDialog.ConfirmDialog />
    </PageShell>
  );
}
